// Keys whose values must never reach the console — debug payloads can carry
// whole request-data contexts (e.g. JSONata inputs) including the session id.
const SENSITIVE_KEYS = new Set([
  'sessionid',
  'sessiontoken',
  'x-session-token',
  'token',
  'authorization',
  'password',
]);

// Plain object = prototype chain of at most one link, so objects from other
// realms (pop-out window) and Object.create(null) are redacted too.
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === null || Object.getPrototypeOf(proto) === null;
};

// `path` detects genuine cycles; `memo` returns the finished copy of a shared
// reference so each source object is traversed once (a memoized copy built
// inside a cyclic subtree may carry a '<circular>' marker from its first path).
const walk = (
  value: unknown,
  path: WeakSet<object>,
  memo: WeakMap<object, unknown>
): unknown => {
  const isArray = Array.isArray(value);
  if (!isArray && !isPlainObject(value)) {
    return value;
  }

  const container = value as object;
  if (path.has(container)) {
    return '<circular>';
  }
  const memoized = memo.get(container);
  if (memoized !== undefined) {
    return memoized;
  }

  path.add(container);
  try {
    const copy = isArray
      ? (value as unknown[]).map((item) => walk(item, path, memo))
      : Object.fromEntries(
          Object.entries(value as Record<string, unknown>).map(
            ([key, entry]) =>
              SENSITIVE_KEYS.has(key.toLowerCase())
                ? [key, '<redacted>']
                : [key, walk(entry, path, memo)]
          )
        );
    memo.set(container, copy);
    return copy;
  } finally {
    path.delete(container);
  }
};

/**
 * Deep copy of a debug payload with sensitive values replaced by '<redacted>'.
 * Recurses only into plain objects and arrays; other instances (Error, Date)
 * pass through untouched so their console rendering stays intact.
 */
export const redactSensitiveKeys = (value: unknown): unknown =>
  walk(value, new WeakSet(), new WeakMap());
